import { useMemo, useEffect } from "react";
import { useFormContext } from "react-hook-form";

interface VisiblePackageType {
  id: string;
}

interface VisibleVehicleType {
  id: string;
  visiblePackageTypeIds?: string[] | null;
}

interface UseVisiblePackageTypesParams<T extends VisiblePackageType> {
  allPackageTypes: T[] | undefined;
  vehicleTypes: VisibleVehicleType[] | undefined;
  arePackageTypesResolved: boolean;
  vehicleTypeFieldName?: string;
  packageTypeFieldName?: string;
  packageItemsFieldName?: string;
}

export function useVisiblePackageTypes<T extends VisiblePackageType>({
  allPackageTypes,
  vehicleTypes,
  arePackageTypesResolved,
  vehicleTypeFieldName = "shipments.0.vehicleTypeId",
  packageTypeFieldName = "shipments.0.packageTypeId",
  packageItemsFieldName,
}: UseVisiblePackageTypesParams<T>) {
  const { watch, getValues, setValue } = useFormContext();
  const watchVehicleTypeId = watch(vehicleTypeFieldName);

  const packageTypes = useMemo(() => {
    if (!allPackageTypes) return undefined;
    if (!watchVehicleTypeId) return allPackageTypes;
    const selectedVehicleType = vehicleTypes?.find(
      (vt) => vt.id === watchVehicleTypeId,
    );
    if (!selectedVehicleType?.visiblePackageTypeIds) return allPackageTypes;
    return allPackageTypes.filter((pt) =>
      selectedVehicleType.visiblePackageTypeIds!.includes(pt.id),
    );
  }, [allPackageTypes, watchVehicleTypeId, vehicleTypes]);

  // Only clear once the available set is actually known. `packageTypes` is
  // undefined while the list is unrequested, in flight, or failed, as well as
  // when it genuinely resolved to nothing — so emptiness alone can't be trusted.
  // Clearing before the list lands would drop a saved selection with nothing to
  // validate it against, and nothing restores it once the list arrives.
  useEffect(() => {
    if (!arePackageTypesResolved) return;
    const currentPackageTypeId = getValues(packageTypeFieldName);
    if (
      currentPackageTypeId &&
      !packageTypes?.some((pt) => pt.id === currentPackageTypeId)
    ) {
      setValue(packageTypeFieldName, undefined);
    }
    if (!packageItemsFieldName) return;
    const items: { packageTypeId?: string | null }[] | undefined =
      getValues(packageItemsFieldName);
    if (!Array.isArray(items) || items.length === 0) return;
    let changed: boolean = false;
    const next: { packageTypeId?: string | null }[] = items.map(
      (item: { packageTypeId?: string | null }) => {
        if (!item?.packageTypeId) return item;
        if (packageTypes?.some((pt) => pt.id === item.packageTypeId)) {
          return item;
        }
        changed = true;
        return { ...item, packageTypeId: null };
      },
    );
    if (changed) {
      setValue(packageItemsFieldName, next);
    }
  }, [
    packageTypes,
    arePackageTypesResolved,
    packageTypeFieldName,
    packageItemsFieldName,
    getValues,
    setValue,
  ]);

  return { packageTypes };
}
